/**
 * REV-798: turning a stored language preference into the active language.
 *
 * The API stores `locale` on a user and on an organization (one of
 * @contract/locales, or null for none). This is the UI half: which stored
 * values the UI can honour, and how to switch to one.
 *
 * Only a language the UI OFFERS is applied (`supportedLanguages` in ./index,
 * which lists a language once its strings ship). The API accepts `de` before
 * German strings exist (REV-801); applying it early would set `i18n.language`
 * to `de`, which date and number formatting follows (src/utils/format.ts), over
 * text that is still English. `<html lang>` follows the RENDERED language
 * (documentLanguage.ts, REV-796, WCAG 3.1.1), so it updates through
 * `languageChanged` with nothing to do here.
 */
import i18n, { supportedLanguages, type SupportedLanguage } from './index';

/** The language when the UI offers it, otherwise undefined. */
export function offeredLanguage(code: string | null | undefined): SupportedLanguage | undefined {
  return code && Object.prototype.hasOwnProperty.call(supportedLanguages, code)
    ? (code as SupportedLanguage)
    : undefined;
}

/**
 * The first language in `languages` (a browser's `navigator.languages`) that
 * the UI offers, matched by language subtag so `de-AT` is `de`.
 */
export function browserLanguage(
  languages: readonly string[] = typeof navigator === 'undefined' ? [] : navigator.languages
): SupportedLanguage | undefined {
  for (const tag of languages) {
    const offered = offeredLanguage(tag.toLowerCase().split('-')[0]);
    if (offered) return offered;
  }
  return undefined;
}

/**
 * Switch the UI to a stored preference.
 *
 * - A locale the UI offers: switch to it.
 * - A locale it does not: change nothing.
 * - `null` (no preference): switch to what the browser asks for, falling back
 *   to English. The detector's own cache is skipped on purpose: it holds the
 *   language last applied, which after clearing a preference is exactly the
 *   one the person no longer wants forced.
 *
 * @param locale - a stored preference, or null for none
 * @param browserLanguages - the browser's language list; for tests
 */
export async function applyLocale(
  locale: string | null | undefined,
  browserLanguages?: readonly string[]
): Promise<void> {
  const target =
    locale === null ? (browserLanguage(browserLanguages) ?? 'en') : offeredLanguage(locale);
  if (target && i18n.language !== target) {
    await i18n.changeLanguage(target);
  }
}
